import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarIcon } from 'lucide-react'
import PropTypes from 'prop-types'

import { Button } from '@/components/elements/button'
import { Skeleton } from '@/components/elements/skeleton'
import { Modal } from '@/components/Modal'
import { getEvents, registerForEvent } from '@/services/events.service'
import { formatTimeRange, formatDate } from '@/utils/formatters'
import { cn, toast } from '@/utils/helpers'

function RegisterForCertificateEventModal({
  studentCertificateId,
  certificateId,
  onClose,
  entityId,
  name,
}) {
  const filters = { certificate: certificateId, entity: entityId }
  // Data
  const { data: events, isPending } = useQuery({
    queryKey: ['events'],
    queryFn: () => getEvents(filters),
  })

  // Data processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const { isPending: registrationPending, mutate: mutateEvent } = useMutation({
    mutationFn: (data) => registerForEvent(data.eventId, [studentCertificateId]),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('You have successfully registered for this event.')
      queryClient.invalidateQueries({ queryKey: ['accounts', 'students', 'certificates'] })
      queryClient.invalidateQueries({ queryKey: ['accounts', 'instructors', 'certificates'] })
      onClose()
    },
  })

  return (
    <Modal
      title={`Register ${name}`}
      size="lg"
      content={
        <div>
          <ul>
            {events?.results.map((event) => (
              <li
                key={event.id}
                className={cn(
                  'bg-background flex flex-row items-center justify-between gap-1 border-x border-b-1 p-2 first:rounded-t-md first:border-t-1 last:rounded-b-md',
                )}
              >
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-semibold">{event.name}</span>

                  <div className="flex flex-row gap-1 text-sm">
                    <CalendarIcon className="stroke-primary size-4" />
                    <p>
                      <strong>Date: </strong> {formatDate(event.date)}
                    </p>
                    <p>
                      <strong>Time: </strong> {formatTimeRange(event.startTime, event.endTime)}
                    </p>
                  </div>
                </div>

                <div className="flex flex-row gap-2">
                  <Button
                    onClick={() => mutateEvent({ eventId: event.id })}
                    disabled={registrationPending}
                  >
                    Register
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          {events?.results.length === 0 && !isPending && (
            <p className="text-sm text-gray-500">
              There are no upcoming events for this certificate.
            </p>
          )}
          {isPending && (
            <div className="flex flex-col gap-2">
              {/* Event row placeholders */}
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          )}
        </div>
      }
      setOpen={onClose}
    />
  )
}

export default RegisterForCertificateEventModal

RegisterForCertificateEventModal.propTypes = {
  studentCertificateId: PropTypes.string.isRequired,
  certificateId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  entityId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
}
